import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Space, Switch, Tooltip, Upload, message } from 'antd';
import { PaperClipOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { uploadNonStdAttachment } from '../../services/nonstandardOps';
import { describeUploadError, ensureUploadSize } from '../../utils/uploadLimits';
import { NonStdAttachment, NonStdEntry, NonStdEntryPayload } from '../../types/nonstandardOps';

interface Props {
  /** 编辑时传入已有条目 */
  initial?: NonStdEntry;
  submitText?: string;
  /** 草稿暂存的键（新增记录时传）：没点"添加记录"之前的内容会暂存在当前浏览器里，刷新页面不丢。
   *  只是单台电脑的便利，已添加的记录始终保存在服务器上，换电脑也能看到 */
  draftKey?: string;
  onSubmit: (payload: NonStdEntryPayload) => Promise<void>;
  onCancel?: () => void;
}

/** 鼠标放在问号上显示：这个开关管什么、和"提交本轮结果"按钮的区别 */
const VISIBLE_HINT = '打开后，客户可以立即在指令详情里看到这条记录。只是让客户“能看到”，不会通知客户，也不影响轮次；默认关闭，即仅内部可见。需要客户查看并反馈时，请点右上角的“提交本轮结果，请客户反馈”。';

interface Draft {
  text: string;
  attachments: NonStdAttachment[];
  visible: boolean;
}

const loadDraft = (key?: string): Partial<Draft> | null => {
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveDraft = (key: string | undefined, draft: Draft, empty: boolean) => {
  if (!key) return;
  try {
    if (empty) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(draft));
  } catch {
    /* 浏览器不允许本地存储时忽略，不影响使用 */
  }
};

/** 新增/修改一条执行记录：文字 + 照片/Excel/PDF/视频等附件，一个开关决定客户能不能看到。 */
const EntryEditor: React.FC<Props> = ({ initial, submitText = '添加记录', draftKey, onSubmit, onCancel }) => {
  const draft = useRef(initial ? null : loadDraft(draftKey)).current;
  const [text, setText] = useState(initial?.text ?? draft?.text ?? '');
  const [attachments, setAttachments] = useState<NonStdAttachment[]>(initial?.attachments ?? draft?.attachments ?? []);
  const [visible, setVisible] = useState<boolean>(initial?.customer_visible ?? draft?.visible ?? false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // 暂存草稿（仅新增时）
  useEffect(() => {
    if (initial || !draftKey) return undefined;
    const timer = setTimeout(() => {
      saveDraft(draftKey, { text, attachments, visible }, !text.trim() && attachments.length === 0);
    }, 400);
    return () => clearTimeout(timer);
  }, [initial, draftKey, text, attachments, visible]);

  const handleSubmit = async () => {
    if (!text.trim() && attachments.length === 0) {
      message.warning('请填写记录内容，或上传照片/附件/视频');
      return;
    }
    setSaving(true);
    try {
      await onSubmit({ text: text.trim(), attachments, customer_visible: visible });
      if (!initial) {
        setText('');
        setAttachments([]);
        setVisible(false);
        if (draftKey) saveDraft(draftKey, { text: '', attachments: [], visible: false }, true);
      }
    } catch (err: any) {
      const data = err?.response?.data;
      const detail = data?.detail || (data && typeof data === 'object' ? Object.values(data).flat().join('；') : '') || err?.message;
      message.error(detail || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file: File) => {
    if (!ensureUploadSize(file)) return false;
    setUploading(true);
    try {
      const uploaded = await uploadNonStdAttachment(file);
      setAttachments(prev => [...prev, uploaded]);
    } catch (err: any) {
      message.error(`“${file.name}”上传失败：${describeUploadError(err)}`);
    } finally {
      setUploading(false);
    }
    return false;
  };

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <Input.TextArea
          rows={3}
          maxLength={2000}
          placeholder="记录做了什么、发现了什么、数量和结果、和客户确认的内容等"
          value={text}
          onChange={e => setText(e.target.value)}
        />
        <Space wrap align="center">
          <Upload multiple showUploadList={false} beforeUpload={handleUpload as any} disabled={uploading || attachments.length >= 20}>
            <Button icon={<PaperClipOutlined />} loading={uploading}>上传照片/Excel/PDF/视频等附件</Button>
          </Upload>
          {attachments.map((att, index) => (
            <span key={att.url} style={{ fontSize: 12 }}>
              {att.name || att.url.split('/').pop()}
              <a style={{ marginLeft: 4 }} onClick={() => setAttachments(prev => prev.filter((_, i) => i !== index))}>移除</a>
            </span>
          ))}
        </Space>
        <Space wrap>
          <span>
            对客户可见
            <Tooltip title={VISIBLE_HINT}>
              <QuestionCircleOutlined style={{ margin: '0 4px', color: '#8c8c8c', cursor: 'help' }} />
            </Tooltip>
            ：
          </span>
          <Switch checked={visible} onChange={setVisible} />
          <Button type="primary" loading={saving} onClick={handleSubmit}>{submitText}</Button>
          {onCancel ? <Button onClick={onCancel}>取消</Button> : null}
        </Space>
        {!initial && draftKey && (text || attachments.length > 0) ? (
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>还没添加的内容暂存在本机浏览器里；点"添加记录"后才会保存到系统，换电脑才能看到。</div>
        ) : null}
      </Space>
    </div>
  );
};

export default EntryEditor;
